import { getConfig } from "@/lib/config";

// Audit P2 portal-order-detail — small embedded Mapbox static-images
// tile shown under Site address. Customers identify the rental by
// "where is it going" as much as by order number; a tiny map under
// the street line orients them at a glance. We use the static-images
// API (a plain <img> URL) rather than mapbox-gl so the page stays
// light and jsdom tests don't have to mock the GL runtime.

const PIN_HEX = "d80027"; // brand-red without the leading `#`
const TILE_WIDTH = 600;
const TILE_HEIGHT = 240;
const TILE_ZOOM = 14;

export function SiteMap({
  lat,
  lng
}: {
  lat: number | null;
  lng: number | null;
}): JSX.Element | null {
  const { mapboxToken } = getConfig();
  if (lat === null || lng === null || !mapboxToken) return null;

  // Static-images URL — `pin-s+<hex>(lng,lat)/lng,lat,zoom/WxH@2x`.
  const center = `${lng},${lat}`;
  const tileUrl =
    `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
    `pin-s+${PIN_HEX}(${center})/${center},${TILE_ZOOM}/` +
    `${TILE_WIDTH}x${TILE_HEIGHT}@2x` +
    `?access_token=${encodeURIComponent(mapboxToken)}`;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div className="mt-3 overflow-hidden rounded-md border border-zinc-200" data-testid="site-map">
      <img
        src={tileUrl}
        alt="Map of the delivery site"
        width={TILE_WIDTH}
        height={TILE_HEIGHT}
        loading="lazy"
        className="block w-full"
        data-testid="site-map-tile"
      />
      <a
        href={directionsUrl}
        target="_blank"
        rel="noreferrer"
        className="block border-t border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-medium text-brand-red hover:bg-zinc-100"
        data-testid="site-map-open-in-maps"
      >
        Open in Maps →
      </a>
    </div>
  );
}
